import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { FOOTER_SECTIONS, SOCIAL_LINKS } from "../data/footerData";
import { StaticImage } from "gatsby-plugin-image";
import { Link } from "gatsby";

const containerVariants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const Footer = () => {
  return (
    <footer className="bg-black text-gray-300 pt-20 pb-10">
      {/* 1. TOP CTA SECTION */}
      <motion.div 
        className="max-w-7xl mx-auto px-6 mb-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-gray-800 pb-12">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tighter mb-6">
              Let's create something <span className="text-red-600">extraordinary</span> together.
            </h2>
            <p className="text-lg text-gray-400">Ready to elevate your brand? Drop us a line or visit our office.</p>
          </div>

          <Link to="/contact">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="mt-8 md:mt-0 group flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full font-bold hover:bg-red-600 hover:text-white transition-colors duration-300"
            >
            Start a Project
            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
          </Link>
        </div>
      </motion.div>

      {/* 2. MAIN LINKS GRID */}
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
        {/* Brand Column */}
        <motion.div 
          className="md:col-span-4"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-3xl font-bold text-white tracking-tighter mb-6">
            <StaticImage
              src="../images/logo-white.png"
              alt="Brand Focus logo"
              className="h-20 w-fit"
              objectFit="contain"
            />
          </div>
          <p className="text-gray-400 mb-6 leading-relaxed">
            We are a full-service creative agency building brands for the digital age. We merge imagination with
            technology to drive results.
          </p>

          {/* Newsletter Signup */}
          <div className="relative mt-8">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 focus:outline-none focus:border-red-600 text-white transition-colors"
            />
            <motion.button 
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-600 p-1.5 rounded-md text-white hover:bg-red-700"
            >
              <ArrowRight size={16} />
            </motion.button>
          </div>
        </motion.div>

        {/* Links Columns (Generated from Data) */}
        <motion.div 
          className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          {FOOTER_SECTIONS.map((section, index) => (
            <motion.div key={index} variants={itemVariants}>
              <h4 className="text-white font-bold text-lg mb-6">{section.title}</h4>
              <ul className="space-y-4">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-red-600 hover:translate-x-1 inline-block transition-all duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* 3. BOTTOM BAR (Socials & Copyright) */}
      <motion.div 
        className="max-w-7xl mx-auto px-6 border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} BrandFocus.lk. All rights reserved.</p>

        {/* Social Icons Loop */}
        <div className="flex items-center gap-6">
          {SOCIAL_LINKS.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.a
                key={index}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, y: -3 }}
                className="text-gray-400 hover:text-white hover:bg-red-600 p-2 rounded-full transition-colors duration-300"
                aria-label="Social Link"
              >
                <Icon size={20} />
              </motion.a>
            );
          })}
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
